// ============================================================
// TATHYA DMS - API Service
// Version: 1.1.0
// Date: 2026-04-22
// Change (v1.1.0): PDF endpoints now take a colId parameter so
//   multiple PDF columns per database are addressable. The legacy
//   (rowId-only) exports are kept as thin wrappers that call the
//   new endpoints after a colId lookup — used by any pre-v2.0
//   UI bits that haven't been updated yet. New code should import
//   the Cell-suffixed versions.
// Change (v1.0.6): added reorderColumns for drag-to-rearrange.
// Change (v1.0.5): Fixed auth interceptors to whitelist only the
//         genuinely-unauthenticated endpoints (login, refresh,
//         forgot-password, reset-password) instead of blacklisting
//         everything under /auth/. change-password lives under
//         /auth/ but requires the JWT; the old logic stripped it,
//         causing "No token provided" on first-login password change.
// Change (v1.0.4): added getUserDirectory (delegation feature)
// Change (v1.0.3): added getCellHistory (Clause 5.3)
// Change (v1.0.2): added unreviewLog (Step 4)
// ============================================================

import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Attach token to every request EXCEPT auth endpoints that are
// genuinely pre-authenticated (login, refresh, forgot-password,
// reset-password). change-password lives under /auth/ too but it
// DOES require the JWT (it's the forced-change-on-first-login flow,
// and the user is already logged in at that point).
const UNAUTHENTICATED_AUTH_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password'
];

api.interceptors.request.use(config => {
  const url = config.url || '';
  const isUnauthEndpoint = UNAUTHENTICATED_AUTH_PATHS.some(p => url.includes(p));
  if (!isUnauthEndpoint) {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiry - only refresh if we have a refresh token
// and the failing request is genuinely an unauthenticated endpoint
// (don't want to refresh the token and retry on /auth/login failures).
// Uses the same whitelist as the request interceptor for consistency.
api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    const url = original.url || '';
    const isUnauthEndpoint = UNAUTHENTICATED_AUTH_PATHS.some(p => url.includes(p));
    const refreshToken = localStorage.getItem('refreshToken');

    if (error.response?.status === 401 && !original._retry && !isUnauthEndpoint && refreshToken) {
      original._retry = true;
      try {
        const res = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
        localStorage.setItem('accessToken', res.data.accessToken);
        original.headers.Authorization = `Bearer ${res.data.accessToken}`;
        return api(original);
      } catch {
        localStorage.clear();
        const orgSlug = window.location.pathname.split('/')[1] || 'consam';
        window.location.href = `/${orgSlug}/login`;
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const changePassword = (data) => api.post('/auth/change-password', data);
export const forgotPassword = (data) => api.post('/auth/forgot-password', data);
export const resetPassword = (data) => api.post('/auth/reset-password', data);

// Platform
export const getOrganisations = () => api.get('/platform/organisations');
export const createOrganisation = (data) => api.post('/platform/organisations', data);
export const suspendOrg = (id) => api.put(`/platform/organisations/${id}/suspend`);
export const reactivateOrg = (id) => api.put(`/platform/organisations/${id}/reactivate`);

// Users
export const getUsers = (org) => api.get(`/${org}/users`);
export const getUserDirectory = (org) => api.get(`/${org}/users/directory`);
export const createUser = (org, data) => api.post(`/${org}/users`, data);
export const deactivateUser = (org, id) => api.put(`/${org}/users/${id}/deactivate`);
export const reactivateUser = (org, id) => api.put(`/${org}/users/${id}/reactivate`);
export const resetUserPassword = (org, id, data) => api.put(`/${org}/users/${id}/reset-password`, data);

// Databases
export const getDatabases = (org) => api.get(`/${org}/databases`);
export const createDatabase = (org, data) => api.post(`/${org}/databases`, data);
export const getDatabase = (org, id) => api.get(`/${org}/databases/${id}`);
export const deleteDatabase = (org, id) => api.delete(`/${org}/databases/${id}`);
export const renameDatabase = (org, id, name) => api.patch(`/${org}/databases/${id}/rename`, { name });
export const transferDatabaseOwnership = (org, id, new_owner_id) =>
  api.patch(`/${org}/databases/${id}/owner`, { new_owner_id });

// Columns
export const addColumn = (org, dbId, data) => api.post(`/${org}/databases/${dbId}/columns`, data);
export const updateColumn = (org, dbId, colId, data) => api.put(`/${org}/databases/${dbId}/columns/${colId}`, data);
export const deleteColumn = (org, dbId, colId) => api.delete(`/${org}/databases/${dbId}/columns/${colId}`);
export const reorderColumns = (org, dbId, columnIds) => api.put(`/${org}/databases/${dbId}/columns/reorder`, { columnIds });

// Rows
export const getRows = (org, dbId, params) => api.get(`/${org}/databases/${dbId}/rows`, { params });
export const getRowFull = (org, dbId, rowId) =>
  api.get(`/${org}/databases/${dbId}/rows/${rowId}/full`);
export const addRow = (org, dbId, data) => api.post(`/${org}/databases/${dbId}/rows`, data);
export const updateRow = (org, dbId, rowId, data) => api.put(`/${org}/databases/${dbId}/rows/${rowId}`, data);
export const deleteRow = (org, dbId, rowId) => api.delete(`/${org}/databases/${dbId}/rows/${rowId}`);

// Permissions
export const getPermissions = (org, dbId) => api.get(`/${org}/databases/${dbId}/permissions`);
export const setPermission = (org, dbId, data) => api.post(`/${org}/databases/${dbId}/permissions`, data);

// Shared Links
export const deletePermission = (org_slug, db_id, perm_id) =>
  api.delete(`/${org_slug}/databases/${db_id}/permissions/${perm_id}`);

export const generateLink = (org, dbId) => api.post(`/${org}/databases/${dbId}/share-link`);
export const revokeLink = (org, dbId) => api.delete(`/${org}/databases/${dbId}/share-link`);

// Audit
export const getAuditLogs = (org, dbId, { offset = 0, limit = 500 } = {}) =>
  api.get(`/${org}/audit/${dbId}`, { params: { offset, limit } });
export const reviewLog = (org, dbId, logId) => api.put(`/${org}/audit/${dbId}/${logId}/review`);
export const unreviewLog = (org, dbId, logId) => api.put(`/${org}/audit/${dbId}/${logId}/unreview`);
export const getCellHistory = (org, dbId, rowId, colId) =>
  api.get(`/${org}/databases/${dbId}/rows/${rowId}/columns/${colId}/history`);

// ── Files (PDF per-cell, v1.1.0) ──────────────────────────────
// Upload the PDF for a specific (row, pdf-column) cell. Upload
// replaces any existing file in that cell (backend logs it as
// PDF_REPLACE vs PDF_UPLOAD in the audit trail).
export const uploadPdfToCell = (org, dbId, rowId, colId, file) => {
  const form = new FormData();
  form.append('pdf', file);
  return api.post(`/files/${org}/${dbId}/${rowId}/${colId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Delete the PDF from a specific (row, pdf-column) cell.
export const deletePdfFromCell = (org, dbId, rowId, colId) =>
  api.delete(`/files/${org}/${dbId}/${rowId}/${colId}`);

// URL for inline PDF view / <iframe src=...>. Pass a JWT via
// ?token= for the exported-Excel flow. The Files router's
// middleware lifts the token into the Authorization header
// before auth runs.
export const getPdfCellUrl = (org, dbId, rowId, colId) =>
  `${API_BASE}/files/${org}/${dbId}/${rowId}/${colId}`;

// ── Files (legacy, pre-v2.0) ──────────────────────────────────
// getPdfUrl (no colId) is kept as a URL builder only — it points
// at the backend's backward-compat GET shim used by pre-migration
// Excel exports. No legacy upload/delete shim exists on the
// backend; the old uploadPdf()/deletePdf() calls were removed
// here because all UI code has been migrated to uploadPdfToCell /
// deletePdfFromCell, which take a colId. If anything still calls
// uploadPdf/deletePdf at runtime that's a bug to surface as a
// ReferenceError rather than silently hit a 404 shim.
export const getPdfUrl = (org, dbId, rowId) =>
  `${API_BASE}/files/${org}/${dbId}/${rowId}`;

export default api;
